import type { UseCaseKey, UseCaseType, OfferingType } from '../types';

export interface UseCaseDef {
  key: UseCaseKey;
  label: string;
  icon: string;
  tag: 'METRICS' | 'LOGS' | 'SYNTHETIC' | 'CUSTOM';
  color: string;
  rgb: string;
  offering: OfferingType;
  type: UseCaseType;
  /** Sub-title shown beneath the label in the selector card */
  subtitle: string;
  /** For generic use cases — parameter columns shown in the host table */
  params?: ParamDef[];
}

export interface ParamDef {
  id: string;
  label: string;
  placeholder?: string;
  isSelect: boolean;
  options?: string[];
  defaultValue?: string;
}

export const USE_CASES: Record<UseCaseKey, UseCaseDef> = {
  sql: {
    key: 'sql',
    label: 'SQL Server',
    icon: '🗄️',
    tag: 'METRICS',
    color: '#16A34A',
    rgb: '22,163,74',
    offering: 'platform',
    type: 'sql',
    subtitle: 'Telegraf SQL Plugin · Standard',
  },
  oracle: {
    key: 'oracle',
    label: 'Oracle DB',
    icon: '🔶',
    tag: 'METRICS',
    color: '#F97316',
    rgb: '249,115,22',
    offering: 'platform',
    type: 'oracle',
    subtitle: 'oracle.txt upload · RITM attachment',
  },
  mqm: {
    key: 'mqm',
    label: 'MQM Fileage',
    icon: '📁',
    tag: 'LOGS',
    color: '#06B6D4',
    rgb: '6,182,212',
    offering: 'platform',
    type: 'mqm',
    subtitle: 'File age / queue monitor · Log collection',
  },
  esxiping: {
    key: 'esxiping',
    label: 'ESXi Ping Monitoring',
    icon: '📡',
    tag: 'SYNTHETIC',
    color: '#F59E0B',
    rgb: '245,158,11',
    offering: 'platform',
    type: 'esxiping',
    subtitle: 'ICMP ping · VMware ESXi hosts · Bulk Add/Remove',
  },
  winsvc: {
    key: 'winsvc',
    label: 'Windows Services',
    icon: '🪟',
    tag: 'METRICS',
    color: '#0078D4',
    rgb: '0,120,212',
    offering: 'custom',
    type: 'generic',
    subtitle: 'Win · Telegraf WinServices Plugin',
    params: [
      { id: 'svcNames', label: 'Service Names', placeholder: 'Spooler, W3SVC or ALL', isSelect: false },
      { id: 'interval', label: 'Interval', isSelect: true, options: ['30s', '60s', '120s', '300s'], defaultValue: '60s' },
    ],
  },
  linuxsvc: {
    key: 'linuxsvc',
    label: 'Linux Services',
    icon: '🐧',
    tag: 'METRICS',
    color: '#E95420',
    rgb: '233,84,32',
    offering: 'custom',
    type: 'generic',
    subtitle: 'Linux · systemd Monitoring Plugin',
    params: [
      { id: 'unitNames', label: 'Unit Names', placeholder: 'nginx.service, sshd.service', isSelect: false },
      { id: 'interval', label: 'Interval', isSelect: true, options: ['30s', '60s', '120s', '300s'], defaultValue: '60s' },
    ],
  },
  urlmon: {
    key: 'urlmon',
    label: 'URL Monitoring',
    icon: '🔗',
    tag: 'SYNTHETIC',
    color: '#10B981',
    rgb: '16,185,129',
    offering: 'custom',
    type: 'generic',
    subtitle: 'Basic HTTP/S probe · confighttp',
    params: [
      { id: 'url', label: 'URL', placeholder: 'https://myapp.lilly.com/health', isSelect: false },
      { id: 'useHttps', label: 'HTTPS?', isSelect: true, options: ['Yes', 'No'], defaultValue: 'Yes' },
      { id: 'port', label: 'Port', placeholder: '443', isSelect: false },
      { id: 'path', label: 'Path', placeholder: '/health', isSelect: false },
      { id: 'noRedirects', label: 'No Redirects?', isSelect: true, options: ['No', 'Yes'], defaultValue: 'No' },
      { id: 'interval', label: 'Interval', isSelect: true, options: ['30s', '60s', '120s', '300s'], defaultValue: '60s' },
    ],
  },
  httpcheck: {
    key: 'httpcheck',
    label: 'URL Monitoring (httpcheck plugin)',
    icon: '🌐',
    tag: 'SYNTHETIC',
    color: '#8B5CF6',
    rgb: '139,92,246',
    offering: 'custom',
    type: 'httpcheck',
    subtitle: 'OTel httpcheck receiver plugin',
  },
  procmon: {
    key: 'procmon',
    label: 'Process Monitoring',
    icon: '⚙️',
    tag: 'METRICS',
    color: '#EC4899',
    rgb: '236,72,153',
    offering: 'custom',
    type: 'procmon',
    subtitle: 'OTel hostmetrics process scraper',
  },
  custom: {
    key: 'custom',
    label: 'Custom / Other',
    icon: '📋',
    tag: 'CUSTOM',
    color: '#A78BFA',
    rgb: '167,139,250',
    offering: 'custom',
    type: 'template',
    subtitle: 'Download template · fill · upload',
  },
};

export const PLATFORM_USE_CASES: UseCaseKey[] = ['sql', 'oracle', 'mqm', 'esxiping'];
export const CUSTOM_USE_CASES: UseCaseKey[] = ['winsvc', 'linuxsvc', 'urlmon', 'httpcheck', 'procmon', 'custom'];

/**
 * Default MQM paths by OS + environment.
 *
 * Lilly's MQM convention partitions per-environment under /home/mqm/
 * on Linux and E:\home\mqm\ on Windows. The 'prd' (not 'prod') segment
 * matches the on-disk directory naming. Source: AIOps catalogue v5
 * spec (Jun 2026).
 */
export const MQM_DEFAULT_PATHS: Record<'Linux' | 'Windows', Record<string, string>> = {
  Linux: {
    prod: '/home/mqm/prd/tmp/',
    qa:   '/home/mqm/qa/tmp/',
    dev:  '/home/mqm/dev/tmp/',
  },
  Windows: {
    prod: 'E:\\home\\mqm\\prd\\tmp\\',
    qa:   'E:\\home\\mqm\\qa\\tmp\\',
    dev:  'E:\\home\\mqm\\dev\\tmp\\',
  },
};

/** SQL Server Agent Config Groups (Step A of the wizard). */
export const SQL_GROUPS = ['AG CONSOLIDATED', 'AG STANDALONE', 'CONSOLIDATED', 'STANDALONE'] as const;
export type SqlGroup = (typeof SQL_GROUPS)[number];

/** Link to the splunk_hcs_SQL README on GitHub */
export const SQL_README = 'https://github.com/EliLillyCo/splunk_hcs_SQL/blob/main/README.md';

/** Variable definition for a single field inside a SQL agent config */
export interface SqlVarDef {
  /** Internal variable key e.g. SQL_HOSTNAME */
  k: string;
  /** Placeholder shown in the input */
  ph: string;
  /** Short description shown next to the key */
  d: string;
}

/** SQL Server Agent Config Files (Step B of the wizard). */
export interface SqlCfgDef {
  label: string;
  file: string;
  vars: SqlVarDef[];
}

export const SQL_CFGS: Record<string, SqlCfgDef> = {
  c1: {
    label: 'Default Instance Only',
    file: 'agent_config_instances_1.yaml',
    vars: [
      { k: 'SQL_HOSTNAME', ph: 'myserver.am.lilly.com', d: 'FQDN' },
      { k: 'SQL_INSTANCE_PORT_DEFAULT', ph: '1433', d: 'Default instance port' },
    ],
  },
  c2: {
    label: 'Default + 1 Named Instance',
    file: 'agent_config_instances_2.yaml',
    vars: [
      { k: 'SQL_HOSTNAME', ph: 'myserver.am.lilly.com', d: 'FQDN' },
      { k: 'SQL_INSTANCE_PORT_DEFAULT', ph: '1433', d: 'Default port' },
      { k: 'SQL_INSTANCE_NAME_1', ph: 'NAMED_INST_1', d: 'Named instance 1' },
      { k: 'SQL_INSTANCE_PORT_1', ph: '1434', d: 'Instance 1 port' },
    ],
  },
  c3: {
    label: '1 Named Instance Only',
    file: 'agent_config_named_instance_only.yaml',
    vars: [
      { k: 'SQL_HOSTNAME', ph: 'myserver.am.lilly.com', d: 'FQDN' },
      { k: 'SQL_INSTANCE_NAME_1', ph: 'NAMED_INST_1', d: 'Instance 1' },
      { k: 'SQL_INSTANCE_PORT_1', ph: '1434', d: 'Instance 1 port' },
    ],
  },
  c4: {
    label: '2 Named Instances',
    file: 'agent_config_named_instance_only_2.yaml',
    vars: [
      { k: 'SQL_HOSTNAME', ph: 'myserver.am.lilly.com', d: 'FQDN' },
      { k: 'SQL_INSTANCE_NAME_1', ph: 'NAMED_INST_1', d: 'Instance 1' },
      { k: 'SQL_INSTANCE_PORT_1', ph: '1434', d: 'Port 1' },
      { k: 'SQL_INSTANCE_NAME_2', ph: 'NAMED_INST_2', d: 'Instance 2' },
      { k: 'SQL_INSTANCE_PORT_2', ph: '1435', d: 'Port 2' },
    ],
  },
  c5: {
    label: '3 Named Instances',
    file: 'agent_config_named_instance_only_3.yaml',
    vars: [
      { k: 'SQL_HOSTNAME', ph: 'myserver.am.lilly.com', d: 'FQDN' },
      { k: 'SQL_INSTANCE_NAME_1', ph: 'NAMED_INST_1', d: 'Instance 1' },
      { k: 'SQL_INSTANCE_PORT_1', ph: '1434', d: 'Port 1' },
      { k: 'SQL_INSTANCE_NAME_2', ph: 'NAMED_INST_2', d: 'Instance 2' },
      { k: 'SQL_INSTANCE_PORT_2', ph: '1435', d: 'Port 2' },
      { k: 'SQL_INSTANCE_NAME_3', ph: 'NAMED_INST_3', d: 'Instance 3' },
      { k: 'SQL_INSTANCE_PORT_3', ph: '1436', d: 'Port 3' },
    ],
  },
  c6: {
    label: '4 Named Instances',
    file: 'agent_config_named_instance_only_4.yaml',
    vars: [
      { k: 'SQL_HOSTNAME', ph: 'myserver.am.lilly.com', d: 'FQDN' },
      { k: 'SQL_INSTANCE_NAME_1', ph: 'NAMED_INST_1', d: 'Instance 1' },
      { k: 'SQL_INSTANCE_PORT_1', ph: '1434', d: 'Port 1' },
      { k: 'SQL_INSTANCE_NAME_2', ph: 'NAMED_INST_2', d: 'Instance 2' },
      { k: 'SQL_INSTANCE_PORT_2', ph: '1435', d: 'Port 2' },
      { k: 'SQL_INSTANCE_NAME_3', ph: 'NAMED_INST_3', d: 'Instance 3' },
      { k: 'SQL_INSTANCE_PORT_3', ph: '1436', d: 'Port 3' },
      { k: 'SQL_INSTANCE_NAME_4', ph: 'NAMED_INST_4', d: 'Instance 4' },
      { k: 'SQL_INSTANCE_PORT_4', ph: '1437', d: 'Port 4' },
    ],
  },
};
